// sort-imports-ignore

import '@formatjs/intl-datetimeformat/polyfill';
import '@formatjs/intl-datetimeformat/locale-data/en';
import { Controller } from '@hotwired/stimulus';
import { get } from '@rails/request.js';

import * as Sentry from '@sentry/react';

import { reloadDocument } from '../lib/desktop_navigation_diagnostics';
import { logDebug, logError, track } from '../lib/logger';
import { currentWorkspaceId, getMetaContent } from '../lib/meta';
import { initSentry } from '../lib/sentry';

const BUILD_CHECK_INTERVAL = 30_000;
const RELOAD_DEBUG_STORAGE_KEY = 'output:reload-debug';
const VISIBILITY_BREADCRUMBS_STORAGE_KEY = 'output:visibility-breadcrumbs';
const VISIBILITY_BREADCRUMBS_MAX = 25;
const REPORTED_RELOAD_CAUSES_STORAGE_KEY = 'output:reported-reload-causes';

const desktopReloadReason = (detail) => {
  if (detail?.source === 'globals' || detail?.source === 'nagbar') return 'client-update-available';
  if (detail?.source === 'shared-channel') {
    return detail.nagbarReason === 'unsupported' ? 'incompatible-client' : 'client-update-available';
  }
  if (detail?.source === 'loading-wrapper' || detail?.source === 'log-viewer') return 'manual-retry';
  if (detail?.source === 'live-journal') return 'local-data-recovery';
  return 'error-boundary-recovery';
};

// Custom error type for unexplained browser reloads and tab discards.
class PageReloadError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PageReloadError';
  }
}

export default class extends Controller {
  build = null;
  build_check = null;

  connect() {
    this.report_navigation_forensics();

    this.bound_output_reload = this.output_reload.bind(this);
    this.bound_visibility_breadcrumb = () => this.append_visibility_breadcrumb(document.visibilityState);
    this.bound_focus_breadcrumb = () => this.append_visibility_breadcrumb('focus');
    this.bound_blur_breadcrumb = () => this.append_visibility_breadcrumb('blur');
    window.addEventListener('output:reload', this.bound_output_reload);
    document.addEventListener('visibilitychange', this.bound_visibility_breadcrumb);
    window.addEventListener('focus', this.bound_focus_breadcrumb);
    window.addEventListener('blur', this.bound_blur_breadcrumb);
    this.append_visibility_breadcrumb('load');

    this.check_build_version();
  }

  disconnect() {
    if (this.build_check) {
      clearTimeout(this.build_check);
      this.build_check = null;
    }

    if (this.bound_output_reload) {
      window.removeEventListener('output:reload', this.bound_output_reload);
    }

    if (this.bound_visibility_breadcrumb) {
      document.removeEventListener('visibilitychange', this.bound_visibility_breadcrumb);
    }

    if (this.bound_focus_breadcrumb) {
      window.removeEventListener('focus', this.bound_focus_breadcrumb);
    }

    if (this.bound_blur_breadcrumb) {
      window.removeEventListener('blur', this.bound_blur_breadcrumb);
    }
  }

  // classifies how this page load started — app-forced reload (persisted record),
  // browser tab discard, or a reload with no app record (user refresh / external) —
  // and pairs it with the visibility breadcrumbs leading up to it.
  report_navigation_forensics() {
    let forcedReload = null;
    try {
      const previousReloadDebug = window.sessionStorage.getItem(RELOAD_DEBUG_STORAGE_KEY);
      if (previousReloadDebug) {
        window.sessionStorage.removeItem(RELOAD_DEBUG_STORAGE_KEY);
        forcedReload = JSON.parse(previousReloadDebug);
      }
    } catch (error) {
      console.error('[reload-debug] Failed to read previous forced reload details', error);
    }

    let recentVisibility = null;
    try {
      const breadcrumbs = window.sessionStorage.getItem(VISIBILITY_BREADCRUMBS_STORAGE_KEY);
      if (breadcrumbs) recentVisibility = JSON.parse(breadcrumbs);
    } catch {
      // sessionStorage unavailable
    }

    const navigationType = performance.getEntriesByType?.('navigation')?.[0]?.type || null;
    const wasDiscarded = document.wasDiscarded === true;

    let cause = navigationType === 'back_forward' ? 'back-forward' : 'navigate';
    if (forcedReload) {
      cause = `app-forced:${forcedReload.detail?.source || 'unknown'}:${forcedReload.detail?.reason || 'unknown'}`;
    } else if (wasDiscarded) {
      cause = 'browser-tab-discarded';
    } else if (navigationType === 'reload') {
      cause = 'reload-without-app-record';
    }

    const forensics = {
      at: new Date().toISOString(),
      cause,
      navigationType,
      wasDiscarded,
      forcedReload,
      recentVisibility,
      url: window.location.href,
      workspaceId: currentWorkspaceId() || null,
    };

    logDebug('reload', 'navigation forensics', forensics);
    if (cause === 'navigate' || cause === 'back-forward') return;

    console.error('[reload-debug] Page load forensics', forensics);
    // Electron owns current desktop navigation diagnostics. Avoid a duplicate
    // Rails issue merely because an expected navigation happened there.
    if (window.electron?.diagnostics || forcedReload || !this.claim_reload_cause_report(cause)) return;

    try {
      initSentry();
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('reload_cause', cause.split(':')[0]);
        scope.setExtra('forensics', forensics);
        // one Sentry issue per distinct cause, so each source/reason trends separately
        scope.setFingerprint(['reload-forensics', cause]);
        Sentry.captureException(new PageReloadError(`page reloaded via ${cause}`));
      });
    } catch (error) {
      console.error('[reload-debug] Failed to report forensics to Sentry', error);
    }
  }

  // Records that a cause has been reported for this tab session and answers whether this call
  // is the one that gets to report it. Returns true when sessionStorage is unavailable, so a
  // browser that can't remember still reports rather than going silent.
  claim_reload_cause_report(cause) {
    try {
      const stored = window.sessionStorage.getItem(REPORTED_RELOAD_CAUSES_STORAGE_KEY);
      const reported = stored ? JSON.parse(stored) : [];
      if (reported.includes(cause)) return false;

      window.sessionStorage.setItem(REPORTED_RELOAD_CAUSES_STORAGE_KEY, JSON.stringify([...reported, cause]));
      return true;
    } catch {
      return true;
    }
  }

  // persisted trail of focus/visibility transitions; survives reloads so forensics
  // can show whether a reload landed right after the app regained or lost focus
  append_visibility_breadcrumb(state) {
    try {
      const previous = window.sessionStorage.getItem(VISIBILITY_BREADCRUMBS_STORAGE_KEY);
      const breadcrumbs = previous ? JSON.parse(previous) : [];
      breadcrumbs.push({ state, at: new Date().toISOString() });
      window.sessionStorage.setItem(VISIBILITY_BREADCRUMBS_STORAGE_KEY, JSON.stringify(breadcrumbs.slice(-VISIBILITY_BREADCRUMBS_MAX)));
    } catch {
      // sessionStorage unavailable
    }
  }

  async check_build_version() {
    // abort if not in a workspace
    if (!currentWorkspaceId()) {
      logDebug('reload', 'build version check skipped', { reason: 'missing_workspace', url: window.location.href });
      return;
    }

    try {
      // build update url
      const response = await get(`https://updates.${getMetaContent('env:default_host')}/build`);
      const data = await response.json;

      if (!(response.ok && data.ok)) {
        logError('globals', 'Failed to fetch build version', data?.errors);
        return;
      }

      const serverBuild = data.data.build;
      if (this.build !== null && this.build.version !== serverBuild.version) {
        // align with server so periodic checks don't re-fire nagbar until version changes again
        const previousBuild = this.build;
        this.build = serverBuild;

        const showRestartNagbar = () =>
          window.dispatchEvent(
            new CustomEvent('output:internal:nagbar', {
              detail: { reason: 'restart', previousBuild, build: serverBuild },
            }),
          );
        const forceHardReload = (error) => {
          window.dispatchEvent(
            new CustomEvent('output:reload', {
              detail: {
                source: 'globals',
                reason: 'service-worker-unregister-failed',
                error: error instanceof Error ? error.message : String(error),
              },
            }),
          );
        };

        // if a service worker is registered, unregister it and show the nagbar.
        // Root scope only: the OneSignal push worker at /push/onesignal/ must
        // never be unregistered here — that would kill the push subscription.
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker
            .getRegistration('/')
            .then((registration) => {
              if (registration) {
                return registration.unregister().then(() => {
                  // wait a bit for unregistration to complete, then show the nagbar
                  setTimeout(() => {
                    showRestartNagbar();
                  }, 100);
                });
              } else {
                // no service worker registered, show the nagbar
                showRestartNagbar();
              }
            })
            // if unregistration fails, force a hard reload
            .catch(forceHardReload);
        } else {
          // no service worker registered, show the nagbar
          showRestartNagbar();
        }
      } else {
        track('buildVersion', serverBuild.version);
        this.build = serverBuild;
      }
    } catch (error) {
      logError('globals', 'Failed to fetch build version:', error);
    } finally {
      // always fire next check
      this.build_check = setTimeout(() => this.check_build_version(), BUILD_CHECK_INTERVAL);
    }
  }

  output_reload(event) {
    const reloadDebug = {
      at: new Date().toISOString(),
      detail: event?.detail || null,
      url: window.location.href,
      visibilityState: document.visibilityState,
      workspaceId: currentWorkspaceId() || null,
    };
    const reloadStack = new Error('output:reload dispatch stack');

    console.error('[reload-debug] Forced reload requested', reloadDebug, reloadStack);
    logDebug('reload', 'output_reload received', { ...reloadDebug, stack: reloadStack.stack });

    try {
      window.sessionStorage.setItem(RELOAD_DEBUG_STORAGE_KEY, JSON.stringify({ ...reloadDebug, stack: reloadStack.stack }));
    } catch (error) {
      console.error('[reload-debug] Failed to persist forced reload details', error);
    }

    // show overlay
    const overlay = document.querySelector('[data-output-reload-pending]');
    if (overlay) overlay.style.display = null;

    // reset button if it was already shown
    const button = document.querySelector('[data-output-reload-pending-button]');
    if (button) {
      button.style.pointerEvents = 'none';
      button.style.cursor = 'default';
      button.style.opacity = 0;
    }

    // show button after a longer delay
    setTimeout(() => {
      const button = document.querySelector('[data-output-reload-pending-button]');
      if (button) {
        button.style.pointerEvents = 'auto';
        button.style.cursor = 'pointer';
        button.style.opacity = 1;
      }
    }, 2500);

    // hard reload the window
    reloadDocument(desktopReloadReason(event?.detail));
  }

  dispatchOutputEvent({ params }) {
    window.dispatchEvent(new CustomEvent(params.eventName, { detail: params.detail }));
  }

}
